import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import AdminLayout from "./components/AdminLayout";
import axios from "axios";
import "./AdminUsers.css";
import "./AdminAddProduct.css";

const PACKID_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const PACKID_LEN = 10;

function generatePackId(len = PACKID_LEN) {
  const bytes = new Uint32Array(len);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < len; i++) bytes[i] = Math.floor(Math.random() * 999999);
  }

  let out = "";
  for (let i = 0; i < len; i++) out += PACKID_CHARS[bytes[i] % PACKID_CHARS.length];
  return out;
}

function generateUniquePackId(existingRows) {
  const existing = new Set(
    (existingRows || [])
      .map((r) => String(r?.packid || "").trim())
      .filter(Boolean)
  );

  let tries = 0;
  while (tries < 20) {
    const id = generatePackId();
    if (!existing.has(id)) return id;
    tries++;
  }
  return generatePackId() + "X";
}

function toNumber(value) {
  const cleaned = String(value ?? "").replace(/[^\d.]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function calcInrPrice(usd, usdToInrRate, profitPercent) {
  const usdNum = toNumber(usd);
  const rateNum = toNumber(usdToInrRate);
  const profitNum = toNumber(profitPercent);

  if (usdNum <= 0 || rateNum <= 0) return "";

  const inrBase = usdNum * rateNum;
  const finalPrice = inrBase + (inrBase * profitNum) / 100;

  return String(Math.round(finalPrice));
}

function extractPackSortValue(item) {
  const pack = String(item?.Pack ?? "").trim();
  const name = String(item?.name ?? "").trim();
  const source = `${pack} ${name}`;
  const matches = source.match(/\d+(\.\d+)?/g);

  if (!matches || matches.length === 0) return Number.MAX_SAFE_INTEGER;
  return Math.min(...matches.map(Number));
}

function sortProductsByPackAmount(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const aVal = extractPackSortValue(a);
    const bVal = extractPackSortValue(b);

    if (aVal !== bVal) return aVal - bVal;

    const aPack = String(a?.Pack ?? "");
    const bPack = String(b?.Pack ?? "");
    return aPack.localeCompare(bPack, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
}

function buildUsdPriceMap(products) {
  const map = new Map();

  for (const item of Array.isArray(products) ? products : []) {
    const packKey = String(item?.Pack || "").trim().toLowerCase();
    const nameKey = String(item?.name || "").trim().toLowerCase();
    const usd = String(item?.price ?? "").trim();

    if (packKey) map.set(`pack:${packKey}`, usd);
    if (nameKey) map.set(`name:${nameKey}`, usd);
  }

  return map;
}

function mergeCostWithApiUsd(existingCost, products) {
  const priceMap = buildUsdPriceMap(products);

  return (Array.isArray(existingCost) ? existingCost : []).map((row) => {
    const idKey = String(row?.id || "").trim().toLowerCase();
    const amountKey = String(row?.amount || "").trim().toLowerCase();

    const matchedUsd =
      priceMap.get(`pack:${idKey}`) ||
      priceMap.get(`name:${amountKey}`) ||
      String(row?.usd_price || "").trim();

    return {
      ...row,
      usd_price: matchedUsd || "",
    };
  });
}

function safeStr(v) {
  const s = String(v ?? "").trim();
  if (!s || s === "undefined" || s === "null") return "";
  return s;
}

const AdminEditProduct = () => {
  const params = useParams();
  const navigate = useNavigate();

  const mainInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [apiGames, setApiGames] = useState([]);
  const [selectedApiGame, setSelectedApiGame] = useState("");
  const [apiProducts, setApiProducts] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [usdToInrRate, setUsdToInrRate] = useState("");
  const [profitPercent, setProfitPercent] = useState("");
  const [resellerProfitPercent, setResellerProfitPercent] = useState("");

  const [productCategories, setProductCategories] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  const [form, setForm] = useState({
    _id: "",
    name: "",
    desc: "",
    gameName: "",
    category: "",
    tag: "",
    stock: "yes",
    image: "",
    banner_image: "",
  });

  const [cost, setCost] = useState([
    {
      packid: generatePackId(),
      id: "",
      amount: "",
      price: "",
      reseller_price: "",
      p_category: "",
      pimg: "",
      usd_price: "",
      requiresUserId: false,
      requiresServerId: false,
      requiresCharName: false,
    },
  ]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const setCostField = (index, key, value) => {
    setCost((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const EMPTY_COST_ROW = () => ({
    packid: generatePackId(),
    id: "",
    amount: "",
    price: "",
    reseller_price: "",
    p_category: "",
    pimg: "",
    usd_price: "",
    requiresUserId: false,
    requiresServerId: false,
    requiresCharName: false,
  });

  const handleAddCostField = (index) => {
    setCost((prev) => {
      const updated = [...prev];
      updated.splice(index + 1, 0, { ...EMPTY_COST_ROW(), packid: generateUniquePackId(prev) });
      return updated;
    });
  };

  const handleRemoveCostField = (index) => {
    setCost((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated.length ? updated : [EMPTY_COST_ROW()];
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      name.startsWith("id-") ||
      name.startsWith("amount-") ||
      name.startsWith("price-") ||
      name.startsWith("reseller_price-") ||
      name.startsWith("pimg-") ||
      name.startsWith("pcat-") ||
      name.startsWith("usd_price-")
    ) {
      const idx = parseInt(name.split("-")[1], 10);

      const key = name.startsWith("amount-")
        ? "amount"
        : name.startsWith("reseller_price-")
        ? "reseller_price"
        : name.startsWith("price-")
        ? "price"
        : name.startsWith("pimg-")
        ? "pimg"
        : name.startsWith("pcat-")
        ? "p_category"
        : name.startsWith("usd_price-")
        ? "usd_price"
        : "id";

      setCostField(idx, key, value);
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBannerFile(file);
  };

  const loadCostDropdowns = async () => {
    try {
      const headers = getAuthHeaders();
      const catReq = axios.get("/api/admin/get-product-category", { headers });
      const galReq = axios.get("/api/image/get-images", { headers });

      const [catRes, galRes] = await Promise.allSettled([catReq, galReq]);

      if (catRes.status === "fulfilled" && catRes.value.data?.success) {
        setProductCategories(Array.isArray(catRes.value.data.data) ? catRes.value.data.data : []);
      } else {
        setProductCategories([]);
      }

      if (galRes.status === "fulfilled" && galRes.value.data?.success) {
        setGalleryImages(Array.isArray(galRes.value.data.data) ? galRes.value.data.data : []);
      } else {
        setGalleryImages([]);
      }
    } catch (err) {
      console.log(err);
      setProductCategories([]);
      setGalleryImages([]);
    }
  };

  const loadApiGames = async () => {
    try {
      setLoadingGames(true);
      const res = await axios.get("/api/games");
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setApiGames(list);
      return list;
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to load games");
      setApiGames([]);
      return [];
    } finally {
      setLoadingGames(false);
    }
  };

  const loadApiProducts = async (gameCode) => {
    try {
      const safeGameCode = safeStr(gameCode);
      if (!safeGameCode) {
        setApiProducts([]);
        return [];
      }

      setLoadingProducts(true);
      const res = await axios.get(`/api/products/${encodeURIComponent(safeGameCode)}`);
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      const sorted = sortProductsByPackAmount(list);
      setApiProducts(sorted);
      return sorted;
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to load products");
      setApiProducts([]);
      return [];
    } finally {
      setLoadingProducts(false);
    }
  };

  const handlePrefillApiPacks = () => {
    if (!safeStr(selectedApiGame)) {
      return message.error("Please select a game first");
    }

    if (!Array.isArray(apiProducts) || apiProducts.length === 0) {
      return message.error("No packs found for selected game");
    }

    const sortedProducts = sortProductsByPackAmount(apiProducts);
    const tempRows = [];

    const mapped = sortedProducts.map((item) => {
      const usd = safeStr(item?.price);

      const row = {
        packid:         generateUniquePackId(tempRows),
        id:             safeStr(item?.Pack),
        amount:         safeStr(item?.name || item?.Pack),
        price:          calcInrPrice(usd, usdToInrRate, profitPercent),
        reseller_price: calcInrPrice(usd, usdToInrRate, resellerProfitPercent),
        p_category:     "",
        pimg:           "",
        usd_price:      usd,
        requiresUserId:   Boolean(item?.requiresUserId),
        requiresServerId: Boolean(item?.requiresServerId),
        requiresCharName: Boolean(item?.requiresCharName),
      };

      tempRows.push(row);
      return row;
    });

    setCost(mapped);

    const selectedGameObj = apiGames.find(
      (g) => safeStr(g?.gamecode) === safeStr(selectedApiGame)
    );

    setForm((prev) => ({
      ...prev,
      gameName: safeStr(selectedGameObj?.gamecode) || safeStr(prev.gameName),
      name: safeStr(prev.name) || safeStr(selectedGameObj?.Name),
    }));

    message.success(`Prefilled ${mapped.length} packs`);
  };

  const applyRateToAllRows = () => {
    const rateNum = toNumber(usdToInrRate);
    if (rateNum <= 0) return message.error("Please enter a valid USD to INR rate");

    setCost((prev) =>
      prev.map((row) => ({
        ...row,
        price:          calcInrPrice(row?.usd_price, usdToInrRate, profitPercent),
        reseller_price: calcInrPrice(row?.usd_price, usdToInrRate, resellerProfitPercent),
      }))
    );

    message.success("Prices applied to all rows (no decimals)");
  };

  const getProduct = async () => {
    try {
      const res = await axios.post("/api/product/get-product", { id: params.id });
      if (!res.data?.success) {
        message.error(res.data?.message || "Failed to load product");
        return;
      }

      const p = res.data.data;

      setForm({
        _id:          p._id,
        name:         safeStr(p.name),
        desc:         safeStr(p.desc),
        gameName:     safeStr(p.gameName),
        category:     safeStr(p.category),
        tag:          safeStr(p.tag),
        stock:        safeStr(p.stock) || "yes",
        image:        safeStr(p.image),
        banner_image: safeStr(p.banner_image),
      });

      const cleanCost = Array.isArray(p.cost)
        ? p.cost.map((x) => ({
            packid:         safeStr(x?.packid) || generatePackId(),
            id:             safeStr(x?.id),
            amount:         safeStr(x?.amount),
            price:          safeStr(x?.price),
            reseller_price: safeStr(x?.reseller_price),
            p_category:     safeStr(x?.p_category),
            pimg:           safeStr(x?.pimg),
            usd_price:      safeStr(x?.usd_price),
            requiresUserId:   Boolean(x?.requiresUserId),
            requiresServerId: Boolean(x?.requiresServerId),
            requiresCharName: Boolean(x?.requiresCharName),
          }))
        : [EMPTY_COST_ROW()];

      const safeCost = cleanCost.length ? cleanCost : [EMPTY_COST_ROW()];

      const games = await loadApiGames();

      const matchedGame = (games || []).find(
        (g) =>
          safeStr(g?.gamecode).toLowerCase() ===
          safeStr(p?.gameName).toLowerCase()
      );

      if (safeStr(matchedGame?.gamecode)) {
        setSelectedApiGame(safeStr(matchedGame.gamecode));

        const products = await loadApiProducts(matchedGame.gamecode);
        const mergedCost = mergeCostWithApiUsd(safeCost, products);

        setCost(mergedCost);
      } else {
        setSelectedApiGame("");
        setApiProducts([]);
        setCost(safeCost);
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to load product");
    }
  };

  useEffect(() => {
    loadCostDropdowns();
    getProduct();
    // eslint-disable-next-line
  }, []);

  const selectedGameObj = apiGames.find(
    (g) => safeStr(g?.gamecode) === safeStr(selectedApiGame)
  );

  const finalGameCode =
    safeStr(selectedGameObj?.gamecode) || safeStr(form.gameName);

  const handleUpdateProduct = async () => {
    if (!safeStr(form._id)) return message.error("Missing product id");
    if (!safeStr(form.name)) return message.error("Name is required");

    const token = localStorage.getItem("token");
    if (!token) {
      message.error("Token missing. Please login again.");
      navigate("/login");
      return;
    }

    if (!safeStr(finalGameCode)) {
      return message.error("Please select a valid game");
    }

    if (!Array.isArray(cost) || cost.length === 0) {
      return message.error("Add at least 1 cost row");
    }

    const badRow = cost.find(
      (c) =>
        !safeStr(c?.packid) ||
        !safeStr(c?.id) ||
        !safeStr(c?.amount) ||
        !safeStr(c?.price)
    );

    if (badRow) {
      return message.error("Cost row missing packid/id/amount/price. Please fill all rows.");
    }

    if (bannerFile && bannerFile.size > 2 * 1024 * 1024) {
      return message.error("Banner image exceeds 2MB limit.");
    }

    const payloadCost = cost.map((row) => ({
      packid:         safeStr(row.packid),
      id:             safeStr(row.id),
      amount:         safeStr(row.amount),
      price:          safeStr(row.price),
      reseller_price: safeStr(row.reseller_price),
      p_category:     safeStr(row.p_category),
      pimg:           safeStr(row.pimg),
      usd_price:      safeStr(row.usd_price),
      requiresUserId:   Boolean(row.requiresUserId),
      requiresServerId: Boolean(row.requiresServerId),
      requiresCharName: Boolean(row.requiresCharName),
    }));

    const formData = new FormData();
    formData.append("id", safeStr(form._id));
    formData.append("name", safeStr(form.name));
    formData.append("desc", safeStr(form.desc));
    formData.append("gameName", safeStr(finalGameCode));
    formData.append("category", safeStr(form.category));
    formData.append("tag",      safeStr(form.tag));
    formData.append("stock", safeStr(form.stock) || "yes");
    formData.append("gameCode", safeStr(selectedApiGame));
    formData.append("source", "api");
    formData.append("cost", JSON.stringify(payloadCost));

    if (selectedFile) formData.append("image", selectedFile);
    if (bannerFile) formData.append("banner_image", bannerFile);

    setLoading(true);
    try {
      const res = await axios.post("/api/product/update-product", formData, {
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.success) {
        message.success(res.data?.message || "Updated");

        if (mainInputRef.current) mainInputRef.current.value = "";
        if (bannerInputRef.current) bannerInputRef.current.value = "";

        setSelectedFile(null);
        setBannerFile(null);

        navigate("/admin-products");
      } else {
        message.error(res.data?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Error updating product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title">
          <h3 className="m-0">Edit Product</h3>
        </div>
        <hr />

        <div className="add-product-container">
          <div className="form-fields mb-3">
            <small className="form-label-small">Product Image <span style={{color:"#aaa",fontWeight:400}}>(leave blank to keep current)</span></small>
            <input
              className="w-100"
              type="file"
              accept=".jpg, .jpeg, .png, .webp"
              name="image"
              ref={mainInputRef}
              onChange={handleFileChange}
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Banner Image <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></small>
            <input
              className="w-100"
              type="file"
              accept=".jpg, .jpeg, .png, .webp"
              name="banner_image"
              ref={bannerInputRef}
              onChange={handleBannerChange}
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Product Name</small>
            <input
              className="w-100"
              name="name"
              onChange={handleChange}
              value={form.name}
              type="text"
              placeholder="Enter product name"
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Description <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></small>
            <textarea
              style={{ border: "1px solid #000" }}
              name="desc"
              cols="30"
              rows="3"
              placeholder="Enter description"
              className="form-control"
              onChange={handleChange}
              value={form.desc}
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Game</small>
            <select
              className="w-100"
              value={selectedApiGame}
              onChange={async (e) => {
                const code = safeStr(e.target.value);
                setSelectedApiGame(code);

                const selectedGame = apiGames.find(
                  (g) => safeStr(g?.gamecode) === code
                );

                setForm((prev) => ({
                  ...prev,
                  gameName: code || safeStr(prev.gameName),
                }));

                const products = await loadApiProducts(code);
                setCost((prev) => mergeCostWithApiUsd(prev, products));
              }}
            >
              <option value="">
                {loadingGames ? "Loading games..." : "Select Game"}
              </option>
              {apiGames.map((item) => (
                <option key={item.gamecode} value={item.gamecode}>
                  {item.Name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Category <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></small>
            <input
              className="w-100"
              type="text"
              name="category"
              value={form.category}
              onChange={handleChange}
              placeholder="Type or pick a category"
              list="category-list"
              autoComplete="off"
            />
            <datalist id="category-list">
              {productCategories.map((c) => (
                <option key={c._id} value={c.name} />
              ))}
            </datalist>
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Tag <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></small>
            <input
              className="w-100"
              type="text"
              name="tag"
              value={form.tag}
              onChange={handleChange}
              placeholder="e.g. Hot, New, Sale"
              autoComplete="off"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div className="form-fields">
              <small className="form-label-small">USD → INR Rate</small>
              <input
                className="w-100"
                type="number"
                min="0"
                step="1"
                value={usdToInrRate}
                onChange={(e) => setUsdToInrRate(e.target.value)}
                placeholder="e.g. 97"
              />
            </div>

            <div className="form-fields">
              <small className="form-label-small">Price Profit %</small>
              <input
                className="w-100"
                type="number"
                min="0"
                step="1"
                value={profitPercent}
                onChange={(e) => setProfitPercent(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>

            <div className="form-fields">
              <small className="form-label-small">Reseller Profit %</small>
              <input
                className="w-100"
                type="number"
                min="0"
                step="1"
                value={resellerProfitPercent}
                onChange={(e) => setResellerProfitPercent(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>

            <div className="form-fields" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="add-product-btn"
                onClick={applyRateToAllRows}
                disabled={cost.length === 0}
              >
                Apply All Prices
              </button>
            </div>

            <div className="form-fields" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="add-product-btn"
                onClick={handlePrefillApiPacks}
                disabled={loadingProducts || !selectedApiGame || apiProducts.length === 0}
              >
                {loadingProducts ? "Loading packs..." : `Prefill Packs (${apiProducts.length})`}
              </button>
            </div>
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Stock Status</small>
            <select onChange={handleChange} value={form.stock} name="stock" className="w-100">
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="cost-section">
            <div className="cost-header">
              <h5 className="m-0">Cost Rows</h5>
              <button type="button" className="cost-refresh" onClick={loadCostDropdowns}>
                Refresh Lists
              </button>
            </div>

            {cost.map((row, index) => (
              <div className="cost-card" key={index}>
                <div className="cost-grid">
                  <div className="cost-field">
                    <label>PackID (auto)</label>
                    <input value={row?.packid || ""} type="text" readOnly />
                  </div>

                  <div className="cost-field">
                    <label>ID</label>
                    <input
                      name={`id-${index}`}
                      onChange={handleChange}
                      value={row?.id || ""}
                      type="text"
                      placeholder="Enter id"
                    />
                  </div>

                  <div className="cost-field">
                    <label>Amount</label>
                    <input
                      name={`amount-${index}`}
                      onChange={handleChange}
                      value={row?.amount || ""}
                      type="text"
                      placeholder="Enter Amount"
                    />
                  </div>

                  <div className="cost-field">
                    <label>USD Price</label>
                    <input
                      name={`usd_price-${index}`}
                      onChange={handleChange}
                      value={row?.usd_price || ""}
                      type="text"
                      placeholder="Enter USD price"
                    />
                  </div>

                  <div className="cost-field">
                    <label>Price</label>
                    <input
                      name={`price-${index}`}
                      onChange={handleChange}
                      value={row?.price || ""}
                      type="text"
                      placeholder="Enter Price"
                    />
                  </div>

                  <div className="cost-field">
                    <label>Reseller Price <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></label>
                    <input
                      name={`reseller_price-${index}`}
                      onChange={handleChange}
                      value={row?.reseller_price || ""}
                      type="text"
                      placeholder="Reseller Price (leave blank = same as Price)"
                    />
                  </div>

                  <div className="cost-field">
                    <label>P-Category (optional)</label>
                    <select
                      name={`pcat-${index}`}
                      value={row?.p_category || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select p-category</option>
                      {productCategories.map((c) => (
                        <option key={c._id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="cost-field">
                    <label>Gallery Image (optional)</label>
                    <select
                      value={row?.pimg || ""}
                      onChange={(e) => setCostField(index, "pimg", e.target.value)}
                    >
                      <option value="">Select gallery image</option>
                      {galleryImages.map((g) => (
                        <option key={g._id} value={g.image || ""}>
                          {g.name || "No name"}{" "}
                          {g.image ? `— ${String(g.image).split("/").pop()}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="cost-actions">
                  <button
                    type="button"
                    className="btn-mini"
                    onClick={() => handleAddCostField(index)}
                  >
                    + Add
                  </button>
                  {index > 0 && (
                    <button
                      type="button"
                      className="btn-mini danger"
                      onClick={() => handleRemoveCostField(index)}
                    >
                      - Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button className="add-product-btn" onClick={handleUpdateProduct} disabled={loading}>
            {loading ? "Updating..." : "Update Product"}
          </button>

          {apiProducts.length > 0 && (
            <div style={{ overflowX: "auto", marginTop: 24 }}>
              <h4 style={{ marginBottom: 12 }}>Available Packs</h4>
              <table className="table mt-3 bg-white text-dark">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Pack</th>
                    <th>USD</th>
                    <th>Calculated INR</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {apiProducts.map((row, idx) => (
                    <tr key={`${row?.gamecode || "game"}-${row?.Pack || idx}`}>
                      <td>{row?.name || "-"}</td>
                      <td>{row?.Pack || "-"}</td>
                      <td>{row?.price ?? "-"}</td>
                      <td>{calcInrPrice(row?.price, usdToInrRate, profitPercent) || "-"}</td>
                      <td>{row?.stockStatus || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEditProduct;